import {
  Button,
  Flex,
  FormLabel,
  HStack,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { RiArrowLeftLine } from "@remixicon/react";
import { UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import omit from "lodash/omit";
import { Control, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import FormInput from "@/common/components/Form/FormInput";
import { RouterLinkButton } from "@/common/components/RouterLinkButton";
import { RulesFormLegacy } from "@/common/components/Rule/RulesFormLegacy";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useEnvironmentsData } from "@/environment/data/useEnvironmentsData";
import { FormStageColorPicker } from "@/stage/components/FormStageColorPicker";
import { stageQueryKeys } from "@/stage/data/stageQueryKeys";
import { useStageData } from "@/stage/data/useStageData";
import {
  useStageCreateMutation,
  useStageUpdateMutation,
} from "@/stage/data/useStagesMutations";
import { ErrorResponse } from "@reflag/shared/api";
import { EnvironmentHeaderDTO } from "@reflag/shared/environmentAPI";
import {
  StageColor,
  StageDTO,
  StagePostArgs,
  StagePostSchema,
} from "@reflag/shared/stageAPI";
import { GlobalSettingsUrl } from "@reflag/shared/urls";

const StageEnvironmentForm = ({
  baseName,
  environment,
  control,
}: {
  baseName: `stageEnvironments.${number}`;
  environment: EnvironmentHeaderDTO;
  control: Control<StagePostArgs>;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${baseName}.customRules`,
  });

  return (
    <RulesFormLegacy
      append={append}
      baseName={baseName}
      environment={environment}
      fields={fields}
      remove={remove}
    />
  );
};

type StageFormProps = {
  environments: EnvironmentHeaderDTO[];
  stage?: StageDTO;
};

const StageForm = ({ environments, stage }: StageFormProps) => {
  const { appId } = useEnvironment();
  const navigate = useNavigate();

  const toast = useToast();
  const queryClient = useQueryClient();
  const createStageMutation = useStageCreateMutation();
  const updateStageMutation = useStageUpdateMutation();

  const form = useForm<StagePostArgs>({
    resolver: zodResolver(StagePostSchema),
    defaultValues: {
      name: stage?.name ?? "",
      color: (stage?.color ?? "red") as StageColor,
      stageEnvironments: environments.map((env) => {
        const stageEnv = stage?.stageEnvironments?.find(
          (se) => se.environment.id === env.id,
        );
        return {
          environmentId: env.id,
          targetingMode: stageEnv?.targetingMode ?? "none",
          customRules:
            stageEnv?.customRules.map((rule) => omit(rule, "id")) ?? [],
          segmentIds: stageEnv?.segmentIds ?? [],
          companyIds: stageEnv?.companyIds ?? [],
          userIds: stageEnv?.userIds ?? [],
        };
      }),
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "stageEnvironments",
  });

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    // Cast to avoid errors as patch is less strict than post but requires an id
    (stage ? updateStageMutation : createStageMutation) as UseMutationResult<
      StageDTO,
      AxiosError<ErrorResponse>,
      StagePostArgs
    >,
    (newStage) => {
      queryClient.setQueryData(
        stageQueryKeys.single(appId, newStage.id),
        newStage,
      );
      toast({
        title: !stage
          ? `Stage "${newStage.name}" created`
          : `Stage "${newStage.name}" updated`,
        status: "success",
      });
      navigate(-1);
    },
    {
      prepareVariables: (values) => {
        return {
          id: stage?.id,
          ...values,
        };
      },
    },
  );

  return (
    <FormProvider {...form}>
      <Flex
        alignItems="flex-start"
        as="form"
        direction="column"
        gap={4}
        w="3xl"
        onSubmit={handleSubmit}
      >
        <FormLabel htmlFor="stage-name">Stage name</FormLabel>
        <HStack alignItems="flex-start" justifyContent="flex-start">
          <FormStageColorPicker name="color" />
          <FormInput id="stage-name" name="name" placeholder="Stage name" />
        </HStack>
        <FormLabel>Rules</FormLabel>
        {fields.map((field, index) => (
          <StageEnvironmentForm
            key={field.id}
            baseName={`stageEnvironments.${index}`}
            control={form.control}
            environment={environments[index]}
          />
        ))}
        <Button type="submit" variant="solid">
          Save
        </Button>
      </Flex>
    </FormProvider>
  );
};

export const Stage = ({ stageId }: { stageId: string }) => {
  const { env } = useEnvironment();
  const {
    data: stage,
    isLoading: isStageLoading,
    isSuccess: isStageSuccess,
  } = useStageData(stageId === "new" ? undefined : stageId);

  const { data: environments = [], isLoading: isEnvironmentsLoading } =
    useEnvironmentsData();

  return (
    <Restricted alertStyle="none" role="admin">
      <RouterLinkButton
        leftIcon={<RiArrowLeftLine size={16} />}
        to={GlobalSettingsUrl(env, "app-stages")}
        variant="ghostDimmed"
      >
        Back to stages
      </RouterLinkButton>
      {isEnvironmentsLoading ? (
        <Spinner />
      ) : stageId === "new" ? (
        <StageForm environments={environments} />
      ) : isStageLoading ? (
        <Spinner />
      ) : isStageSuccess ? (
        <StageForm environments={environments} stage={stage} />
      ) : (
        <Text>Error loading stage</Text>
      )}
    </Restricted>
  );
};
